import { useEffect, useRef } from "react";

import type {
  ElementoBarras,
  ElementoCaixa,
  ElementoImagem,
  ElementoQrcode,
  ElementoTexto,
  EtiquetaDesign,
  EtiquetaElemento,
} from "@/types/etiqueta-designer";
import {
  bitmapDeElemento,
  desenharBitmapNoCanvas,
  dimensoesElemento,
  dotsParaMm,
  rotacionarBitmap,
  type ContextoEtiqueta,
} from "@/utils/zplDesigner";

type Props = {
  design: EtiquetaDesign;
  ctx: ContextoEtiqueta;
  zoom: number;
  grade: boolean;
  snap: boolean;
  selecionadoId: string | null;
  onSelecionar: (id: string | null) => void;
  onMover: (id: string, x: number, y: number) => void;
  onRedimensionar: (id: string, largura: number, altura: number) => void;
  onFinalizarMover: () => void;
};

const PASSO_SNAP = 0.5; // mm

const arredondar = (v: number, snap: boolean) =>
  snap
    ? Math.round(v / PASSO_SNAP) * PASSO_SNAP
    : Math.round(v * 100) / 100;

function ImagemElemento({ el }: { el: ElementoImagem }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (!ref.current) return;
    desenharBitmapNoCanvas(
      rotacionarBitmap(bitmapDeElemento(el), el.rotacao),
      ref.current,
    );
  }, [el]);
  return (
    <canvas
      ref={ref}
      className="w-full h-full"
      style={{ imageRendering: "pixelated" }}
    />
  );
}

function TextoElemento({
  el,
  escala,
}: {
  el: ElementoTexto;
  escala: number;
}) {
  const alturaPx = el.alturaFonte * escala;
  const proporcaoNatural = el.fonte === "0" ? 0.58 : 0.6;
  const fatorX =
    el.alturaFonte > 0
      ? el.larguraFonte / el.alturaFonte / proporcaoNatural
      : 1;
  return (
    <div
      className="whitespace-pre leading-none"
      style={{
        fontFamily:
          el.fonte === "0"
            ? "'Arial Narrow', Helvetica, Arial, sans-serif"
            : "'Courier New', monospace",
        fontSize: `${alturaPx}px`,
        lineHeight: 1,
        color: el.inverso ? "#fff" : "#000",
        background: el.inverso ? "#000" : "transparent",
        transform: `scaleX(${fatorX})`,
        transformOrigin: "top left",
      }}
    >
      {el.texto || " "}
    </div>
  );
}

function BarrasElemento({
  el,
  escala,
}: {
  el: ElementoBarras;
  escala: number;
}) {
  const alturaBarrasPx = el.altura * escala;
  return (
    <div className="w-full h-full flex flex-col items-stretch overflow-hidden">
      {el.mostrarTexto && el.textoAcima && (
        <div
          className="text-center font-mono text-black truncate"
          style={{ fontSize: `${Math.max(6, 2.5 * escala)}px` }}
        >
          {el.dados}
        </div>
      )}
      <div
        style={{
          height: `${alturaBarrasPx}px`,
          backgroundImage:
            "repeating-linear-gradient(90deg,#000 0 2px,transparent 2px 4px,#000 4px 5px,transparent 5px 8px)",
        }}
      />
      {el.mostrarTexto && !el.textoAcima && (
        <div
          className="text-center font-mono text-black truncate"
          style={{ fontSize: `${Math.max(6, 2.5 * escala)}px` }}
        >
          {el.dados}
        </div>
      )}
    </div>
  );
}

function QrElemento({ el }: { el: ElementoQrcode }) {
  return (
    <div
      className="w-full h-full bg-white"
      style={{
        backgroundImage:
          "linear-gradient(45deg,#000 25%,transparent 25%,transparent 75%,#000 75%),linear-gradient(45deg,#000 25%,transparent 25%,transparent 75%,#000 75%)",
        backgroundSize: "22% 22%",
        backgroundPosition: "0 0, 11% 11%",
        border: "1px solid #000",
      }}
      title={el.dados}
    />
  );
}

function CaixaElemento({
  el,
  escala,
  dpmm,
}: {
  el: ElementoCaixa;
  escala: number;
  dpmm: number;
}) {
  const espessuraPx = Math.max(1, dotsParaMm(el.espessura, dpmm) * escala);
  const cor = el.inverso ? "#fff" : "#000";
  if (el.preenchido || el.tipo === "linha") {
    return <div className="w-full h-full" style={{ background: cor }} />;
  }
  return (
    <div
      className="w-full h-full"
      style={{
        border: `${espessuraPx}px solid ${cor}`,
        borderRadius: el.arredondamento ? `${el.arredondamento * 2}px` : 0,
      }}
    />
  );
}

export function CanvasEtiqueta({
  design,
  ctx,
  zoom,
  grade,
  snap,
  selecionadoId,
  onSelecionar,
  onMover,
  onRedimensionar,
  onFinalizarMover,
}: Props) {
  const escala = zoom; // px por mm
  const larguraPx = ctx.larguraMm * escala;
  const alturaPx = ctx.alturaMm * escala;
  const arrasto = useRef<{
    id: string;
    modo: "mover" | "redimensionar";
    inicioX: number;
    inicioY: number;
    origX: number;
    origY: number;
    origLargura: number;
    origAltura: number;
  } | null>(null);

  useEffect(() => {
    const mover = (e: PointerEvent) => {
      const a = arrasto.current;
      if (!a) return;
      const dx = (e.clientX - a.inicioX) / escala;
      const dy = (e.clientY - a.inicioY) / escala;
      if (a.modo === "mover") {
        onMover(
          a.id,
          arredondar(a.origX + dx, snap),
          arredondar(a.origY + dy, snap),
        );
      } else {
        onRedimensionar(
          a.id,
          Math.max(0, arredondar(a.origLargura + dx, snap)),
          Math.max(0, arredondar(a.origAltura + dy, snap)),
        );
      }
    };
    const soltar = () => {
      if (!arrasto.current) return;
      arrasto.current = null;
      onFinalizarMover();
    };
    window.addEventListener("pointermove", mover);
    window.addEventListener("pointerup", soltar);
    return () => {
      window.removeEventListener("pointermove", mover);
      window.removeEventListener("pointerup", soltar);
    };
  }, [escala, snap, onMover, onRedimensionar, onFinalizarMover]);

  const iniciarArrasto = (
    e: React.PointerEvent,
    el: EtiquetaElemento,
    modo: "mover" | "redimensionar",
  ) => {
    if (el.bloqueado) return;
    e.stopPropagation();
    e.preventDefault();
    const dim = dimensoesElemento(el, ctx);
    arrasto.current = {
      id: el.id,
      modo,
      inicioX: e.clientX,
      inicioY: e.clientY,
      origX: el.x,
      origY: el.y,
      origLargura: dim.largura,
      origAltura: dim.altura,
    };
    onSelecionar(el.id);
  };

  return (
    <div className="w-full overflow-auto rounded-md border bg-muted/40 p-6">
      <div
        className="relative mx-auto bg-white shadow-sm"
        style={{
          width: `${larguraPx}px`,
          height: `${alturaPx}px`,
          backgroundImage: grade
            ? `linear-gradient(to right, rgba(0,0,0,.08) 1px, transparent 1px),
               linear-gradient(to bottom, rgba(0,0,0,.08) 1px, transparent 1px),
               linear-gradient(to right, rgba(0,0,0,.16) 1px, transparent 1px),
               linear-gradient(to bottom, rgba(0,0,0,.16) 1px, transparent 1px)`
            : undefined,
          backgroundSize: grade
            ? `${escala}px ${escala}px, ${escala}px ${escala}px, ${escala * 10}px ${escala * 10}px, ${escala * 10}px ${escala * 10}px`
            : undefined,
          outline: "1px solid rgba(0,0,0,.25)",
        }}
        onPointerDown={() => onSelecionar(null)}
      >
        {design.elementos.map((el) => {
          if (el.oculto) return null;
          const dim = dimensoesElemento(el, ctx);
          const selecionado = el.id === selecionadoId;
          return (
            <div
              key={el.id}
              className={`absolute ${
                el.bloqueado ? "cursor-not-allowed" : "cursor-move"
              }`}
              style={{
                left: `${el.x * escala}px`,
                top: `${el.y * escala}px`,
                width: `${dim.largura * escala}px`,
                height: `${dim.altura * escala}px`,
                outline: selecionado
                  ? "1.5px solid #2563eb"
                  : "1px dashed rgba(37,99,235,.25)",
                outlineOffset: "1px",
                zIndex: selecionado ? 20 : 1,
              }}
              onPointerDown={(e) => iniciarArrasto(e, el, "mover")}
            >
              <div className="w-full h-full overflow-visible pointer-events-none">
                {el.tipo === "texto" && (
                  <TextoElemento el={el} escala={escala} />
                )}
                {el.tipo === "barras" && (
                  <BarrasElemento el={el} escala={escala} />
                )}
                {el.tipo === "qrcode" && <QrElemento el={el} />}
                {el.tipo === "imagem" && <ImagemElemento el={el} />}
                {(el.tipo === "caixa" || el.tipo === "linha") && (
                  <CaixaElemento el={el} escala={escala} dpmm={ctx.dpmm} />
                )}
                {el.tipo === "bruto" && (
                  <div className="w-full h-full flex items-center justify-center rounded border border-dashed border-amber-500 bg-amber-50 text-[9px] text-amber-700 px-1 text-center">
                    ZPL bruto
                  </div>
                )}
              </div>

              {selecionado && !el.bloqueado && el.tipo !== "bruto" && (
                <div
                  className="absolute -right-1.5 -bottom-1.5 h-3 w-3 rounded-sm border border-white bg-blue-600 cursor-se-resize"
                  onPointerDown={(e) => iniciarArrasto(e, el, "redimensionar")}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
